import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ArrowLeftCircle, Check, CheckCircle2, Phone, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CHECKLIST_ITEMS, DEAL_PHASES, PRIORITY_BADGE } from "@/lib/constants";
import { formatINR } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Card = ({ title, children, testId }) => (
  <section className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-6 sm:p-8" data-testid={testId}>
    {title && <h2 className="font-heading text-lg font-semibold text-emerald-950 mb-5 pb-3 border-b border-slate-100">{title}</h2>}
    {children}
  </section>
);

const Stepper = ({ phase }) => {
  const currentIdx = DEAL_PHASES.indexOf(phase);
  return (
    <div className="flex items-center gap-0 overflow-x-auto py-2" data-testid="deal-stepper">
      {DEAL_PHASES.map((p, i) => (
        <div key={p} className="flex items-center shrink-0">
          {i > 0 && <div className={`h-0.5 w-8 sm:w-14 ${i <= currentIdx ? "bg-emerald-700" : "bg-slate-200"}`} />}
          <div className="flex flex-col items-center gap-1.5 px-1">
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors ${
                i < currentIdx
                  ? "bg-emerald-700 border-emerald-700 text-white"
                  : i === currentIdx
                  ? "bg-emerald-900 border-emerald-900 text-white ring-4 ring-emerald-100"
                  : "bg-white border-slate-300 text-slate-400"
              }`}
              data-testid={`step-${p.replace(/\s/g, "-")}`}
            >
              {i < currentIdx ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-[11px] whitespace-nowrap ${i === currentIdx ? "font-semibold text-emerald-950" : "text-slate-500"}`}>{p}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function DealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [confirmPhase, setConfirmPhase] = useState(null);
  const [dropOpen, setDropOpen] = useState(false);
  const [dropReason, setDropReason] = useState("");

  const { data: deal, isLoading, isError, error } = useQuery({
    queryKey: ["deal", id],
    queryFn: async () => (await api.get(`/deals/${id}`)).data,
    retry: (count, e) => ![403, 404].includes(e?.response?.status) && count < 2,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: isAdmin,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["deal", id] });
    queryClient.invalidateQueries({ queryKey: ["deals"] });
    queryClient.invalidateQueries({ queryKey: ["deals-closed"] });
    queryClient.invalidateQueries({ queryKey: ["leads"] });
  };
  const onError = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  const phaseMutation = useMutation({
    mutationFn: async (phase) => (await api.patch(`/deals/${id}/phase`, { phase })).data,
    onSuccess: (d) => { refresh(); toast.success(`Moved to ${d.phase}`); },
    onError,
  });
  const checklistMutation = useMutation({
    mutationFn: async ({ field, value }) => (await api.patch(`/deals/${id}/checklist`, { [field]: value })).data,
    onSuccess: refresh,
    onError,
  });
  const outcomeMutation = useMutation({
    mutationFn: async ({ outcome, dropped_reason }) => (await api.post(`/deals/${id}/outcome`, { outcome, dropped_reason })).data,
    onSuccess: (d) => { refresh(); toast.success(`Deal marked ${d.outcome}`); setDropOpen(false); },
    onError,
  });
  const reassignMutation = useMutation({
    mutationFn: async (assigned_to) => (await api.patch(`/deals/${id}/reassign`, { assigned_to })).data,
    onSuccess: () => { refresh(); toast.success("Deal reassigned"); },
    onError,
  });

  if (isError) {
    return (
      <div className="p-8" data-testid="deal-error">
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-5 max-w-lg">
          <p className="font-medium text-red-700">
            {formatApiErrorDetail(error?.response?.data?.detail) || "Could not load this deal."}
          </p>
          <button onClick={() => navigate("/deals")} className="mt-3 text-sm text-emerald-800 font-medium hover:underline" data-testid="deal-error-back">
            ← Back to Deals Pipeline
          </button>
        </div>
      </div>
    );
  }
  if (isLoading || !deal) return <div className="p-8 text-slate-400">Loading…</div>;

  const isActive = deal.outcome === "Active";
  const phaseIdx = DEAL_PHASES.indexOf(deal.phase);
  const checklistCount = CHECKLIST_ITEMS.filter((c) => deal[c.field]).length;

  const tryMove = (targetPhase) => {
    if (deal.phase === "Discussion" && checklistCount < 5 && DEAL_PHASES.indexOf(targetPhase) > phaseIdx) {
      setConfirmPhase(targetPhase);
    } else {
      phaseMutation.mutate(targetPhase);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl" data-testid="deal-detail-page">
      <button onClick={() => navigate("/deals")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-900 mb-4 transition-colors" data-testid="back-to-deals">
        <ArrowLeft className="h-4 w-4" /> Deals Pipeline
      </button>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-emerald-950 tracking-tight" data-testid="deal-title">
          {deal.customer_name} ↔ {deal.property_title}
        </h1>
        <Badge
          variant="outline"
          className={isActive ? "border-sky-300 text-sky-800 bg-sky-50" : deal.outcome === "Done" ? "border-emerald-400 text-emerald-900 bg-emerald-100" : "border-red-200 text-red-600 bg-red-50"}
          data-testid="deal-outcome-badge"
        >
          {deal.outcome}
        </Badge>
        {deal.lead_priority && <Badge variant="outline" className={PRIORITY_BADGE[deal.lead_priority]}>{deal.lead_priority}</Badge>}
      </div>

      {!isActive && (
        <div className={`rounded-xl border px-5 py-4 mb-6 ${deal.outcome === "Done" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="deal-closed-banner">
          <p className="font-medium">
            {deal.outcome === "Done" ? "This deal was completed" : "This deal was dropped"}
            {deal.closed_at && ` on ${new Date(deal.closed_at).toLocaleDateString("en-IN", { dateStyle: "long" })}`}.
          </p>
          {deal.dropped_reason && <p className="text-sm mt-1">Reason: {deal.dropped_reason}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card testId="deal-lead-summary">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Lead</p>
          <button onClick={() => navigate(`/leads/${deal.lead_id}`)} className="font-heading text-lg font-semibold text-emerald-900 hover:underline text-left" data-testid="deal-lead-link">
            {deal.customer_name}
          </button>
          <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {deal.lead_phone}</p>
          <p className="text-sm text-slate-500 mt-1">{deal.lead_requirement}</p>
        </Card>
        <Card testId="deal-property-summary">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Property</p>
          <button onClick={() => navigate(`/properties/${deal.property_id}`)} className="font-heading text-lg font-semibold text-emerald-900 hover:underline text-left" data-testid="deal-property-link">
            {deal.property_title}
          </button>
          <p className="text-sm text-slate-600 mt-1">{deal.property_address}</p>
          <p className="text-sm font-medium text-emerald-900 mt-1">
            {formatINR(deal.property_price)}{deal.property_listing_type === "Rent" ? "/mo" : ""}
          </p>
        </Card>
      </div>

      <Card testId="deal-assignment-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400">Assigned To</p>
            <p className="font-medium text-slate-800 mt-0.5" data-testid="deal-assigned-name">{deal.assigned_to_name || "Unassigned"}</p>
          </div>
          {isAdmin && (
            <Select value={deal.assigned_to || ""} onValueChange={(v) => reassignMutation.mutate(v)}>
              <SelectTrigger data-testid="deal-reassign-select" className="rounded-lg w-[220px]">
                <SelectValue placeholder="Reassign to…" />
              </SelectTrigger>
              <SelectContent>
                {users.filter((u) => u.is_active).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </Card>

      <div className="mt-6 space-y-6">
        <Card title="Pipeline Phase">
          <Stepper phase={deal.phase} />
          <div className="flex flex-wrap gap-3 mt-6">
            <Button
              onClick={() => tryMove(DEAL_PHASES[phaseIdx + 1])}
              disabled={!isActive || phaseIdx >= DEAL_PHASES.length - 1 || phaseMutation.isPending}
              data-testid="advance-phase-button"
              className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white"
            >
              Advance to next phase <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => phaseMutation.mutate(DEAL_PHASES[phaseIdx - 1])}
              disabled={!isActive || phaseIdx === 0 || phaseMutation.isPending}
              data-testid="back-phase-button"
              className="rounded-lg"
            >
              <ArrowLeftCircle className="h-4 w-4 mr-2" /> Move back a phase
            </Button>
            <div className="flex-1" />
            <Button
              onClick={() => outcomeMutation.mutate({ outcome: "Done" })}
              disabled={!isActive || deal.phase !== "Final Payment" || outcomeMutation.isPending}
              data-testid="mark-done-button"
              className="rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Deal Done
            </Button>
            <Button
              variant="outline"
              onClick={() => { setDropReason(""); setDropOpen(true); }}
              disabled={!isActive}
              data-testid="mark-dropped-button"
              className="rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <XCircle className="h-4 w-4 mr-2" /> Mark Deal Dropped
            </Button>
          </div>
        </Card>

        <Card title={`Discussion Checklist (${checklistCount}/5)`} testId="deal-checklist-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CHECKLIST_ITEMS.map((c) => (
              <label key={c.field} className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 cursor-pointer hover:border-emerald-300 transition-colors">
                <Checkbox
                  checked={!!deal[c.field]}
                  disabled={!isActive || checklistMutation.isPending}
                  onCheckedChange={(v) => checklistMutation.mutate({ field: c.field, value: !!v })}
                  data-testid={`checklist-${c.field}`}
                />
                <span className="text-sm text-slate-700">{c.label}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card title="Activity Log" testId="deal-activity-log">
          {(deal.activity_log || []).length === 0 ? (
            <p className="text-slate-400 text-sm">No activity yet.</p>
          ) : (
            <ul className="space-y-3">
              {[...deal.activity_log].reverse().map((a, i) => (
                <li key={i} className="border-l-2 border-emerald-200 pl-4">
                  <p className="text-slate-800 text-sm">{a.detail}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {a.by_name || "Staff"} · {new Date(a.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <AlertDialog open={!!confirmPhase} onOpenChange={(open) => !open && setConfirmPhase(null)}>
        <AlertDialogContent className="rounded-xl" data-testid="advance-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discussion tasks incomplete</AlertDialogTitle>
            <AlertDialogDescription>
              Some discussion tasks aren't marked complete yet ({checklistCount}/5 done) — move to {confirmPhase} anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="advance-confirm-button"
              className="rounded-lg bg-emerald-900 hover:bg-emerald-950"
              onClick={() => { phaseMutation.mutate(confirmPhase); setConfirmPhase(null); }}
            >
              Move Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dropOpen} onOpenChange={setDropOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="drop-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-emerald-950">Mark Deal Dropped</DialogTitle>
            <DialogDescription>This closes the deal and marks the lead Closed-Lost. Optionally note why.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={dropReason}
            onChange={(e) => setDropReason(e.target.value)}
            placeholder="Reason (optional) — e.g. customer chose another property"
            data-testid="drop-reason-input"
            className="rounded-lg"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDropOpen(false)} className="rounded-lg">Cancel</Button>
            <Button
              onClick={() => outcomeMutation.mutate({ outcome: "Dropped", dropped_reason: dropReason })}
              disabled={outcomeMutation.isPending}
              data-testid="drop-confirm-button"
              className="rounded-lg bg-red-600 hover:bg-red-700 text-white"
            >
              {outcomeMutation.isPending ? "Saving…" : "Drop Deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
